"use client";

import SpinnerMini from "@/components/my-ui/fallbacks/SpinnerMini";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { usePromoCodeActions } from "@/hooks/usePromoCodeActions";
import { cn } from "@/lib/utils";
import { ChevronDown, Tags, X } from "lucide-react";
import { useTranslations } from "next-intl";

interface PropsType {
  couponContainerStyle?: string;
  titleStyle?: string;
  inputStyle?: string;
  buttonStyle?: string;
  couponTextStyle?: string;
  couponBadgeStyle?: string;
  discountTextStyle?: string;
}

function ApplyCouponCode({
  titleStyle,
  inputStyle,
  buttonStyle,
  couponBadgeStyle,
  couponTextStyle,
  discountTextStyle,
  couponContainerStyle,
}: PropsType) {
  const t = useTranslations("Shared.cartSheet.couponCode");

  const {
    register,
    handleSubmit,
    errors,
    onSubmit,
    isPromoApplying,
    showCouponCollapsible,
    setShowCouponCollapsible,
    promoCodeData,
    handleRemoveCoupon,
  } = usePromoCodeActions();

  return (
    <>
      {/* Coupon Code Collapsible */}
      <Collapsible
        className={cn(`CollapsibleContent group mb-2`, couponContainerStyle)}
        open={showCouponCollapsible}
        onOpenChange={setShowCouponCollapsible}
      >
        <CollapsibleTrigger className="flex w-full items-center justify-between transition-all hover:text-primary">
          <p
            className={cn(`font-montserrat text-lg font-semibold`, titleStyle)}
          >
            {t(`getDiscountCode`)}
          </p>
          <ChevronDown
            size={20}
            className="transition-all group-data-[state=open]:rotate-180"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <form
            className="flex items-center gap-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Input
              placeholder={t(`couponPlaceholder`)}
              className={cn(inputStyle)}
              disabled={isPromoApplying}
              type="text"
              {...register("coupon", {
                required: t(`validation.required`),
                maxLength: {
                  value: 20,
                  message: t(`validation.invalid`),
                },
                validate: (value) =>
                  !value.includes(" ") || t(`validation.noSpace`),
              })}
            />
            <Button
              type="submit"
              className={cn(buttonStyle)}
              disabled={isPromoApplying}
            >
              {isPromoApplying ? <SpinnerMini /> : t(`applyBtn`)}
            </Button>
          </form>
          {errors?.coupon && (
            <span className="text-xs text-destructive">
              {errors.coupon.message}
            </span>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Applied Coupon */}
      {promoCodeData && (
        <div className="mb-3 flex items-center justify-between">
          <p
            className={cn(
              `flex items-center gap-2 font-montserrat text-lg font-medium`,
              couponTextStyle,
            )}
          >
            <Tags size={20} />
            {t(`couponText`)}
            <span
              className={cn(
                `rounded-md bg-primary-accent px-3 py-1 text-sm`,
                couponBadgeStyle,
              )}
            >
              {promoCodeData.promo_code}
            </span>
            <button
              className="text-muted-foreground hover:text-foreground"
              onClick={handleRemoveCoupon}
            >
              <X size={16} />
            </button>
          </p>

          {/* discount after coupon */}
          <span
            className={cn(
              `flex items-center justify-center rounded-[6px] bg-primary/15 px-[12px] py-[4px] text-base font-medium text-primary`,
              discountTextStyle,
            )}
          >
            {promoCodeData.discount}% {t(`discountText`)}
          </span>
        </div>
      )}
    </>
  );
}

export default ApplyCouponCode;
